import { FeatureGate } from '@orn/global/featureGates/featureGates.js';
import { useBreakpoint } from '@orn/shared-web/hooks/useBreakpoint';
import { cx } from '@orn/shared-web/utils/cx';
import * as React from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';

import ImpersonatorNotice from '../components/ImpersonatorNotice';
import {
  ICON_RAIL_WIDTH_PX,
  MAIN_CONTENT_MAX_WIDTH_PX,
  MAIN_SIDEBAR_WIDTH_PX,
  MOBILE_TOP_BAR_HEIGHT_PX,
} from '../components/navigation/dimensions';
import MainSidebar from '../components/navigation/MainSidebar';
import SyncingNotice from '../components/SyncingNotice';
import { useFinancialAccounts } from '../hooks/queries/useFinancialAccounts';
import { useOnboardingState } from '../hooks/queries/useOnboardingState';
import { useFeatureGates } from '../hooks/useFeatureGates';
import { useHeaderScrollShrink } from '../hooks/useHeaderScrollShrink';
import AssistantPane from '../panes/assistant/AssistantPane';
import { PATHS } from '../paths';
import { useAssistantSidebar, type AssistantPlacement } from '../providers/AssistantSidebarProvider';

/** Left-sidebar placement width — flush panel between nav and content. */
const ASSISTANT_SIDEBAR_WIDTH_PX = 460;
/** Right-module placement width — inset card to the right of content. */
const ASSISTANT_MODULE_WIDTH_PX = 480;

interface IAssistantSidePanelProps {
  placement: AssistantPlacement;
}

/**
 * The assistant rendered beside content at `xl+`. `left` is a flush,
 * full-height panel between the nav and content; `right` is an inset card to
 * the right of content. Both mount {@link AssistantPane} (and its single live
 * WebSocket) once — `HomeScene` guarantees only one is rendered at a time.
 */
const AssistantSidePanel = ({ placement }: IAssistantSidePanelProps) => {
  if (placement === 'right') {
    return (
      <div style={{ width: ASSISTANT_MODULE_WIDTH_PX }} className="flex h-full shrink-0 flex-col py-3 pl-1 pr-3">
        <div className="border-secondary bg-utility-mist-100 flex min-h-0 flex-1 flex-col overflow-hidden rounded-2xl border">
          <AssistantPane />
        </div>
      </div>
    );
  }
  return (
    <div
      style={{ width: ASSISTANT_SIDEBAR_WIDTH_PX }}
      className="border-secondary bg-utility-mist-100 flex h-full shrink-0 flex-col overflow-hidden border-r"
    >
      <AssistantPane />
    </div>
  );
};

const HomeScene = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // Settings is a left-anchored form surface; the rest of the app centers its
  // content column. Drop the centering on the settings route so the form hugs
  // the left edge instead of floating in the middle on wide viewports.
  const anchorContentLeft = location.pathname.startsWith(PATHS.SETTINGS);
  const { scrollerRef, onScroll } = useHeaderScrollShrink();
  const { flags, loading: flagsLoading } = useFeatureGates();
  const isOnboardingV2 = flags.has(FeatureGate.OnboardingV2);

  // The assistant docks beside content only at `xl+`. Below that there is no
  // room for a side panel — the nav button navigates to the `/assistant` route
  // instead (see MainApp's AssistantRouteGate). `isXl` here is the same signal
  // that gates the route redirect, so the panel and the route never both mount
  // AssistantPane (and its single WebSocket).
  const { isEnabled: assistantEnabled, isOpen: assistantOpen, placement: assistantPlacement } = useAssistantSidebar();
  const isXl = useBreakpoint('xl');
  const showAssistantPanel = assistantEnabled && isXl && assistantOpen;

  // Both hooks fire unconditionally — React forbids conditional hooks, and
  // this branch collapses to one once `feat-onboarding-v2` graduates to
  // ALL_USERS and the legacy path is removed.
  const { financialAccounts, loading: financialAccountsLoading } = useFinancialAccounts();
  const { needsOnboarding, loading: onboardingStateLoading } = useOnboardingState();

  const checking =
    (flagsLoading && flags.size === 0) || (isOnboardingV2 ? onboardingStateLoading : financialAccountsLoading);
  const shouldOnboard = isOnboardingV2 ? needsOnboarding : financialAccounts.length === 0;

  React.useEffect(() => {
    if (!checking && shouldOnboard) {
      void navigate(PATHS.ONBOARDING);
    }
  }, [checking, shouldOnboard, navigate]);

  // Hold the shell until the check resolves and the about-to-redirect window
  // closes — otherwise the main app flashes in before the navigate-effect
  // above lands an onboarding-needing viewer at `/onboarding`.
  if (checking || shouldOnboard) {
    return null;
  }

  return (
    <>
      <MainSidebar />
      {/*
       * Outer wrapper reserves space for the fixed `MainSidebar` via padding
       * (top bar height below `md`, icon rail at `md`, full sidebar at `lg+`).
       * `flex-1` covers the case where this scene mounts inside a flex-row
       * parent — notably the Storybook global decorator
       * (`app-bg flex h-full w-full`). Without it the wrapper would shrink to
       * its content's intrinsic width and leave a right-side gutter at wide
       * viewports. In production the scene mounts directly inside `#root`
       * (block context, no flex parent), where `flex-1` is a no-op and
       * `h-full` + block-level width handle layout — but keeping `flex-1`
       * makes the layout robust to either context. `min-w-0` lets the inner
       * content shrink below intrinsic min-width so panes can reflow at
       * narrower viewports without overflowing right.
       *
       * The area beside the sidebar is full-bleed so the scroller can place its
       * vertical scrollbar at the page's right edge; the `MAIN_CONTENT_MAX_WIDTH_PX`
       * cap + centering live on the inner content track (and the notices track),
       * not on a shared centering ancestor.
       *
       * At `xl+` with the assistant panel open this wrapper becomes a flex-row:
       * the panel docks beside the content column (left = between nav and
       * content, right = past content) and the content column holds the notices
       * track + scroller. For right placement the panel — not the scroller —
       * sits at the page's right edge; the scroller's load-bearing props
       * (`relative`, `[overflow-anchor:none]`) travel with the scroller div
       * regardless, so they are unaffected.
       *
       * All four magic numbers come from `navigation/dimensions.ts` — change a
       * value there and the sidebar components, top bar, and these offsets all
       * update together.
       */}
      <div
        style={
          {
            '--nav-top-bar-h': `${MOBILE_TOP_BAR_HEIGHT_PX}px`,
            '--nav-rail-w': `${ICON_RAIL_WIDTH_PX}px`,
            '--nav-full-w': `${MAIN_SIDEBAR_WIDTH_PX}px`,
            '--nav-content-max-w': `${MAIN_CONTENT_MAX_WIDTH_PX}px`,
          } as React.CSSProperties
        }
        className={cx(
          'pt-(--nav-top-bar-h) md:pl-(--nav-rail-w) lg:pl-(--nav-full-w) flex h-full min-w-0 flex-1 md:pt-0',
          showAssistantPanel ? 'flex-row' : 'flex-col'
        )}
      >
        {showAssistantPanel && assistantPlacement === 'left' && <AssistantSidePanel placement="left" />}
        {/*
         * Content column: notices track + scroller. A flex child so it sits
         * beside the assistant panel when one is shown, and the sole column
         * (full width) otherwise. `min-h-0` keeps the scroller bounded; `min-w-0`
         * lets panes reflow when the panel narrows the available width.
         */}
        <div className="flex min-h-0 min-w-0 flex-1 flex-col">
          {/*
           * Notices are app-wide banners, not pane content, so they stay pinned
           * above the scroller. They ride their own capped track so they line up
           * with the pane content below.
           */}
          <div
            className={cx('max-w-(--nav-content-max-w) flex w-full min-w-0 flex-col', !anchorContentLeft && 'mx-auto')}
          >
            <ImpersonatorNotice />
            <SyncingNotice />
          </div>
          {/*
           * The single scroll context for the route (ORN-548 keeps html/body
           * locked). It is full-bleed — no `max-w` — so its vertical scrollbar
           * sits at the page's right edge. The inner track caps the content at
           * `MAIN_CONTENT_MAX_WIDTH_PX` and centers it (or hugs the left edge on
           * Settings). `min-h-0` lets this flex item be bounded so it actually
           * scrolls rather than growing past the viewport; `overflow-x-hidden`
           * contains panes that don't manage their own horizontal overflow.
           *
           * `overflow-anchor: none` is load-bearing: the sticky EntityPaneHeader
           * shrinks as scrollTop rises, which shortens the scroll content; the
           * browser's scroll anchoring would "compensate" by walking scrollTop
           * back, un-shrinking the header — an oscillation loop that also blocks
           * scrolling entirely near the top.
           *
           * `relative` is load-bearing too: react-aria's visually-hidden inputs
           * are `position: absolute`; an unpositioned scroller lets them escape
           * to the page, where focusing one scrolls `<html>` itself despite the
           * overflow lock.
           */}
          <div
            ref={scrollerRef}
            onScroll={onScroll}
            className="relative flex min-h-0 w-full min-w-0 flex-1 flex-col overflow-y-auto overflow-x-hidden [overflow-anchor:none]"
          >
            <div
              className={cx(
                'max-w-(--nav-content-max-w) flex h-full w-full min-w-0 flex-col',
                !anchorContentLeft && 'mx-auto'
              )}
            >
              <Outlet />
            </div>
          </div>
        </div>
        {showAssistantPanel && assistantPlacement === 'right' && <AssistantSidePanel placement="right" />}
      </div>
    </>
  );
};

export default HomeScene;
